import { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { REPORT_TYPES } from "../../studyTableRows.consts";
import { FIELD_KEYS } from "../../../study-table-columns/columns.consts";
import { useAppContext } from "context/app.context";
import { StudyData } from "../../../studyTable.types";
import { useUserType } from "hooks/useUserType";
import { USER_TYPES } from "consts";
import { updateStudyValues } from "services/studies/studies";
import { DefaultSpinner } from "components/loading/DefaultSpinner";
import { createReport } from "services/google-docs/createReport";
import getColourByType from "utils/Colours";

interface GoogleDocControllerProps {
  reportType: string;
  studyData: StudyData;
  iconColourInverted: boolean;
}
export const GoogleDocController = ({
  reportType,
  studyData,
  iconColourInverted,
}: GoogleDocControllerProps) => {
  const { iconUrl, isLoading, createReportOnClick, openReport, reportId } =
    useGoogleControllerProps(reportType, studyData, iconColourInverted);

  if (isLoading) {
    return <DefaultSpinner spinnerSize={25} />;
  }

  const [showPreview, setShowPreview] = useState(false);
  const { userTypeColour } = useUserType();
  const [iframeStyle, setIframeStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLImageElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (showPreview) {
      setShowPreview(false);
      return;
    }

    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const padding = 10;
      const availableHeight = window.innerHeight - buttonRect.bottom - padding;

      setIframeStyle({
        position: "fixed",
        top: `${buttonRect.bottom + padding}px`,
        left: "50%",
        transform: "translateX(-50%)",
        width: "90vw",
        height: `${availableHeight}px`,
        border: `5px solid ${userTypeColour}`,//"1px solid #ccc",
        backgroundColor: "#fff",
        boxShadow:  "5px 5px 10px rgba(0, 0, 0, 0.5)",
        zIndex: 9999
      });
      setShowPreview(true);
    }
  };
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        iframeRef.current &&
        !iframeRef.current.contains(e.target as Node) &&
        !buttonRef.current?.contains(e.target as Node)
      ) {
        setShowPreview(false);
      }
    };

    if (showPreview) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("contextmenu", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("contextmenu", handleClickOutside);
    };
  }, [showPreview]);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
    <img
      title={`${reportId ? "edit" : "add"} ${reportType} report`}
      src={iconUrl}
      height="30"
      style={{
        cursor: "pointer",
        //filter: `${
        //  iconColourInverted === true ?
        //    "invert(1) brightness(2) drop-shadow(3px 3px 1px rgb(0 0 0 / 0.1))"
        //    : "drop-shadow(3px 3px 1px rgb(0 0 0 / 0.1))"
        //}`,
      }}
      onContextMenu={handleRightClick}
      ref={buttonRef}
      //onMouseEnter={() => setShowPreview(true)}
      //onMouseLeave={() => setShowPreview(false)}
      onClick={reportId ? openReport : createReportOnClick}
    />
    {showPreview && reportId && (
      <iframe
        src={`https://docs.google.com/document/d/${reportId}/preview`}
        title="Report Preview"
        ref={iframeRef}
        style={iframeStyle}
      />
    )}
    </div>
  );
};

const useGoogleControllerProps = (reportType, studyData: StudyData, iconColourInverted) => {
  const initialReportId = useMemo(() => {
    if (
      reportType === REPORT_TYPES.REFERRAL &&
      studyData[FIELD_KEYS.REFERRAL_REPORT_IDS]
    ) {
      return studyData[FIELD_KEYS.REFERRAL_REPORT_IDS];
    } else if (
      reportType === REPORT_TYPES.MEDICAL &&
      studyData[FIELD_KEYS.MEDICAL_REPORT_IDS]
    ) {
      return studyData[FIELD_KEYS.MEDICAL_REPORT_IDS];
    }
    return null;
  }, [studyData, reportType]);

  const [reportId, setReportId] = useState<string | null>(initialReportId);
  const { userType } = useUserType();
  const iconUrl = useMemo(() => {
    //const BASE_URL = "../assets/images/icons/table-icons/";
    const BASE_URL = "/assets/images/icons/table-icons/";
    const addOrEdit = reportId ? "" : "add-";
    const userColour = getColourByType(userType).replace("#", "");
    switch (reportType) {
      case REPORT_TYPES.MEDICAL:
        //return `${BASE_URL}${addOrEdit}medical-report-icon.svg`;
        if(reportId) {
          return `${BASE_URL}medical-report-icon_${userColour}.svg`;
        } else {
          if(iconColourInverted) {
            return `${BASE_URL}medical-report-icon_${'FFFFFF'}.svg`;
          } else {
            return `${BASE_URL}medical-report-icon_${'000000'}.svg`;
          };
        };
      case REPORT_TYPES.REFERRAL:
      default:
        //return `${BASE_URL}${addOrEdit}referral-report-icon.svg`;
        if(reportId) {
          return `${BASE_URL}referral-report-icon_${userColour}.svg`;
        } else {
          if(iconColourInverted) {
            return `${BASE_URL}referral-report-icon_${'FFFFFF'}.svg`;
          } else {
            return `${BASE_URL}referral-report-icon_${'000000'}.svg`;
          };
        };
    }
  }, [reportType, reportId, userType]);

  const {
    state: { userId },
  } = useAppContext();
  const clearDocumentId = useClearDocumentId(studyData.studypk, reportType);
  const openReport = useCallback(() => {
    clearDocumentId();
    window.open(
      `https://docs.google.com/document/d/${reportId}/edit`,
      "_blank"
    );
  }, [reportId, clearDocumentId]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const createReportOnClick = useCallback(async () => {
    setIsLoading(true);
    const reportId = await createReport(studyData, reportType, userId);
    setReportId(reportId);
    setIsLoading(false);
  }, [reportType, studyData, userId, setReportId, setIsLoading]);

  return { iconUrl, createReportOnClick, openReport, reportId, isLoading };
};

const useClearDocumentId = (studypk, reportType) => {
  // ONLY CLEARS REPORT IF DEVELOPER
  const {
    state: { userId },
  } = useAppContext();
  const { userType } = useUserType();
  return useCallback(async () => {
    if (userType === USER_TYPES.DEVELOPER && userId === "atxpublic" && false) { //false => manually turned off
      const fieldKey =
        reportType === REPORT_TYPES.MEDICAL
          ? FIELD_KEYS.MEDICAL_REPORT_IDS
          : FIELD_KEYS.REFERRAL_REPORT_IDS;
      const result = await updateStudyValues(studypk, { [fieldKey]: null });

      return result;
    }
  }, [userType, studypk, reportType, userId]);
};
